import { TOP_LEVEL_TOT_TEST_IDS } from '@/top-level-tot/constants';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { Button as RSuiteButton } from 'rsuite';

function calcTimeDiffFromNow(timeStamp: string): number {
  return dayjs(timeStamp).diff(dayjs());
}

type WithClick = {
  onClick?: () => void;
  href?: never;
  target?: never;
};

type WithLink = {
  onClick?: never;
  href?: string;
  target: '_blank';
};

type ButtonProps = { label: string } & (WithClick | WithLink);

function Button(props: ButtonProps) {
  return (
    <RSuiteButton
      data-testid={TOP_LEVEL_TOT_TEST_IDS.DOWNLOAD_BUTTON}
      appearance="primary"
      href={props.href}
      target={props.target}
      onClick={props.onClick}
    >
      {props.label}
    </RSuiteButton>
  );
}

export type TOTReportDownloadButtonProps = {
  downloadLink?: string;
  downloadLinkExpiration?: string;
  isFetchingDownloadLink: boolean;
  downloadLinkFetchFailed: boolean;
  refetchDownloadLink: () => void;
};

function DownloadButton(
  props: Pick<TOTReportDownloadButtonProps, 'downloadLink'>
) {
  return (
    <Button label="Download Report" href={props.downloadLink} target="_blank" />
  );
}

function LoadingButton() {
  return <Button label="Generating download link" />;
}

function ErrorRefetchButton(
  props: Pick<
    TOTReportDownloadButtonProps,
    'downloadLinkFetchFailed' | 'refetchDownloadLink'
  >
) {
  return (
    <Button label="Refetch download link" onClick={props.refetchDownloadLink} />
  );
}

export function TOTReportDownloadButton(props: TOTReportDownloadButtonProps) {
  const [hasLinkExpired, setHasLinkExpired] = useState(false);

  const downloadLink = useMemo(() => {
    try {
      new URL(props.downloadLink ?? '');
      return props.downloadLink;
    } catch {
      return null;
    }
  }, [props.downloadLink]);

  useEffect(() => {
    if (!props.downloadLinkExpiration) return setHasLinkExpired(false);

    setHasLinkExpired(calcTimeDiffFromNow(props.downloadLinkExpiration) <= 0);

    const timeOut = setTimeout(() => {
      setHasLinkExpired(true);
    }, calcTimeDiffFromNow(props.downloadLinkExpiration));

    return function cleanUpTimeout() {
      clearTimeout(timeOut);
    };
  }, [props.downloadLinkExpiration]);

  if (props.isFetchingDownloadLink) return <LoadingButton />;

  if (props.downloadLinkFetchFailed || hasLinkExpired || !downloadLink)
    return <ErrorRefetchButton {...props} />;

  return <DownloadButton {...props} />;
}
